import type { CSSProperties } from 'react';
import { Viewport } from '@/theme/breakpoints';
import { getBackgroundGradient } from '@/theme/gradients';
import { pxToRem } from '@/theme/spacing';
import { zIndex } from '@/theme/zIndex';
import {
  NAVBAR_HEIGHT,
  BLACK_PAGE_BACKGROUND_COLOR,
  CSS_POINTER_EVENTS_AUTO,
  CSS_POINTER_EVENTS_NONE,
  NO_BACKGROUND_GRADIENT,
  STAGE_BACKGROUND_IMAGE,
  TRANSPARENT_PAGE_BACKGROUND_COLOR,
  NAVBAR_CENTER_TRANSFORM_HIDDEN,
  NAVBAR_CENTER_TRANSFORM_VISIBLE,
  NAVBAR_TRANSLATE_Y_HIDDEN,
  NAVBAR_TRANSLATE_Y_VISIBLE,
  STAGE_MOBILE_CLASS_NAME,
  STAGE_NON_MOBILE_CLASS_NAME,
  STAGE_NON_ROOT_PAGE_CLASS_NAME,
  STAGE_ROOT_PAGE_CLASS_NAME,
} from './Cpagelayout.constants';

interface BuildRootStyleInput {
  vh: number;
  viewport: Viewport | null | undefined;
  useBlackBackground: boolean;
  useStageBackground: boolean;
}

interface BuildContentStyleInput {
  vh: number;
  isCenterModalOpen: boolean;
}

interface BuildNavbarStylesInput {
  isCenterModalOpen: boolean;
}

interface GetStageClassNameInput {
  isRootStagePage: boolean;
  useStageBackground: boolean;
  viewport: Viewport | null | undefined;
}

interface CenterModalOpenInput {
  isCenterModalOpen: boolean;
}

export interface NavbarStyles {
  base: CSSProperties;
  left: CSSProperties;
  right: CSSProperties;
  center: CSSProperties;
  logoLink: CSSProperties;
  logoMotion: CSSProperties;
}

function getCenterModalPointerEvents({
  isCenterModalOpen,
}: CenterModalOpenInput): 'none' | 'auto' {
  return isCenterModalOpen ? CSS_POINTER_EVENTS_NONE : CSS_POINTER_EVENTS_AUTO;
}

function getCenterModalOpacity({
  isCenterModalOpen,
}: CenterModalOpenInput): number {
  return isCenterModalOpen ? 0 : 1;
}

function getCenterModalTranslateY({
  isCenterModalOpen,
}: CenterModalOpenInput): string {
  return isCenterModalOpen ? NAVBAR_TRANSLATE_Y_HIDDEN : NAVBAR_TRANSLATE_Y_VISIBLE;
}

function getNavbarCenterTransform({
  isCenterModalOpen,
}: CenterModalOpenInput): string {
  return isCenterModalOpen
    ? NAVBAR_CENTER_TRANSFORM_HIDDEN
    : NAVBAR_CENTER_TRANSFORM_VISIBLE;
}

export function getStageClassName({
  isRootStagePage,
  useStageBackground,
  viewport,
}: GetStageClassNameInput): string | undefined {
  if (!useStageBackground) {
    return undefined;
  }

  const stageViewportClassName = viewport === Viewport.Mobile
    ? STAGE_MOBILE_CLASS_NAME
    : STAGE_NON_MOBILE_CLASS_NAME;
  const stageRouteClassName = isRootStagePage
    ? STAGE_ROOT_PAGE_CLASS_NAME
    : STAGE_NON_ROOT_PAGE_CLASS_NAME;

  return `${stageViewportClassName} ${stageRouteClassName}`;
}

export function buildRootStyle({
  vh,
  viewport,
  useBlackBackground,
  useStageBackground,
}: BuildRootStyleInput): CSSProperties {
  const backgroundGradient = viewport ? getBackgroundGradient(viewport) : NO_BACKGROUND_GRADIENT;

  return {
    display: 'flex',
    flexDirection: 'column',
    minHeight: vh * 100,
    height: '100%',
    width: '100%',
    // The stage gradient and the viewport gradient are images; only the black
    // page is a flat colour. Split across both longhands so the key set stays
    // stable across rerenders — see no-background-style-shorthand.
    backgroundColor:
      !useStageBackground && useBlackBackground ? BLACK_PAGE_BACKGROUND_COLOR : TRANSPARENT_PAGE_BACKGROUND_COLOR,
    backgroundImage: useStageBackground
      ? STAGE_BACKGROUND_IMAGE
      : useBlackBackground
        ? NO_BACKGROUND_GRADIENT
        : backgroundGradient,
    position: 'relative',
    overflow: 'hidden',
  };
}

export function buildContentStyle({
  vh,
  isCenterModalOpen,
}: BuildContentStyleInput): CSSProperties {
  return {
    position: 'relative',
    width: '100%',
    height: 100 * vh,
    overflow: 'auto',
    scrollbarWidth: 'none',
    opacity: isCenterModalOpen ? 0 : 1,
    transition: 'opacity 200ms ease-in-out',
    scrollSnapType: 'y mandatory',
  };
}

export function buildNavbarStyles({
  isCenterModalOpen,
}: BuildNavbarStylesInput): NavbarStyles {
  const centerModalOpacity = getCenterModalOpacity({ isCenterModalOpen });
  const navbarAnimationStyle: CSSProperties = {
    opacity: centerModalOpacity,
    transform: getCenterModalTranslateY({ isCenterModalOpen }),
    transition: 'opacity 300ms ease-out, transform 300ms ease-out',
  };

  return {
    base: {
      position: 'relative',
      height: pxToRem(NAVBAR_HEIGHT),
      padding: `0 ${pxToRem(16)}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      zIndex: zIndex.navbar,
      pointerEvents: getCenterModalPointerEvents({ isCenterModalOpen }),
    },
    left: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      flex: 1,
      minWidth: 0,
      ...navbarAnimationStyle,
    },
    right: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      flex: 1,
      minWidth: 0,
      ...navbarAnimationStyle,
    },
    center: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      zIndex: 1,
      left: '50%',
      transform: getNavbarCenterTransform({ isCenterModalOpen }),
      opacity: centerModalOpacity,
      transition: 'opacity 300ms ease-out, transform 300ms ease-out',
    },
    logoLink: {
      display: 'inline-flex',
      alignItems: 'center',
      textDecoration: 'none',
    },
    logoMotion: {
      display: 'inline-flex',
      alignItems: 'center',
    },
  };
}
